import { useTranslation } from "react-i18next";
import PrimaryButton from "../buttons/primaryButton.tsx";
import React from "react";
import useWindowWidthThreshold from "../hooks/useWindowWidthThreshold.ts";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import PricingSummary from "../shared/PricingSummary";
import SlowRequestNotice from "../shared/SlowRequestNotice";
import "../../styles/basket-footer.scss";
import { usePreorderContext } from "../context/PreorderContext";
import type { DepositRequired, CustomerPreOrder } from "../../../common/jsonRpcTypes";

interface BasketItem {
  menu_id: string;
  quantity: number;
}

interface BasketInfoProps {
  totalCost: number | undefined;
  totalCostWithService: number | undefined;
  goToNextStep: () => void;
  items?: BasketItem[];
  itemsCount?: number;
  isOrderValid?: boolean;
  serviceChargeAmount?: number;
  serviceChargePercentage?: number;
  /** When true, hide the service charge row from the footer pricing (shown elsewhere, e.g. basket ServiceChargeSelector). */
  hideServiceCharge?: boolean;
  /** When true, the parent is performing an async submission — disable the button to prevent duplicate clicks. */
  isSubmitting?: boolean;
  /** Deposit information - when present with a preorder, allows proceeding with empty basket */
  depositRequired?: DepositRequired;
  /** Customer preorder - when provided, takes precedence over context value */
  customerPreOrder?: CustomerPreOrder;
  /**
   * When true, at least one basket item is required to proceed, regardless of menu rules or
   * deposit configuration. Used by the standalone place-your-order flow where a pre-order is
   * mandatory. Defaults to false to preserve the /book flow behaviour (empty basket allowed
   * with a deposit or rule-free menus).
   */
  requireBasketItems?: boolean;
  /**
   * Hides the "Place your order" CTA — set when the booking's outcome is
   * already unknown (a timed-out `/submitBooking`).
   */
  submitHidden?: boolean;
}

const BasketFooter: React.FC<BasketInfoProps> = ({
  goToNextStep,
  totalCost,
  totalCostWithService,
  items = [],
  itemsCount = 0,
  isOrderValid = true,
  serviceChargeAmount = 0,
  serviceChargePercentage = 0,
  hideServiceCharge = false,
  isSubmitting = false,
  depositRequired,
  customerPreOrder: customerPreOrderProp,
  requireBasketItems = false,
  submitHidden = false,
}) => {
  const { currency, countryCode, hidePricesByMenu, customerPreOrder: customerPreOrderContext } = usePreorderContext();
  const { t } = useTranslation();
  const showFooterInfo = useWindowWidthThreshold(1024);
  const isBasketEmpty = itemsCount === 0;
  // Use prop customerPreOrder if provided, otherwise fall back to context
  const customerPreOrder = customerPreOrderProp ?? customerPreOrderContext;

  // Get preorderMenus from customerPreOrder to ensure validation rules work correctly
  const preorderMenus = React.useMemo(() => {
    return customerPreOrder?.menus ?? [];
  }, [customerPreOrder]);

  // Helper function to check if there are no minimum preorder rules
  // This logic matches usePreorderState.ts to ensure consistency
  const hasNoPreorderRules = React.useMemo(() => {
    if (!preorderMenus.length) return true;

    // Check if any menu has minimum spend requirements
    const hasMinimumSpendRequirements = preorderMenus.some((menu) => (menu.minimum_preorder_amount ?? 0) > 0);

    // Check if any menu has item limit requirements (only min items are blocking, not max)
    const hasItemLimitRequirements = preorderMenus.some((menu) => {
      return menu.min_items && menu.min_items > 0;
    });

    return !hasMinimumSpendRequirements && !hasItemLimitRequirements;
  }, [preorderMenus]);

  // Allow proceeding with empty basket when deposit is configured AND no validation requirements exist
  const canProceedWithEmptyBasket = isBasketEmpty && depositRequired && hasNoPreorderRules;
  // Allow proceeding with empty basket when there are preorder menus with no rules (even without deposit)
  const canProceedWithEmptyBasketNoRules =
    isBasketEmpty && !depositRequired && hasNoPreorderRules && preorderMenus.length > 0;
  // Only allow proceeding with no preorder menus if there's truly no preorder data at all
  const canProceedWithNoPreorderMenus = isBasketEmpty && !depositRequired && !customerPreOrder;
  // When the flow mandates a pre-order (e.g. place-your-order), an empty basket can never proceed.
  const canProceedWithEmptyBasketForFlow =
    !requireBasketItems &&
    (canProceedWithEmptyBasket || canProceedWithEmptyBasketNoRules || canProceedWithNoPreorderMenus);
  const isDisabled = (isBasketEmpty && !canProceedWithEmptyBasketForFlow) || !isOrderValid || isSubmitting;
  // To be removed when the full booking journey is done
  const showBackButton = false;

  const renderPricing = () => (
    <PricingSummary
      variant="footer"
      subtotal={totalCost}
      totalWithService={totalCostWithService}
      serviceChargeAmount={hideServiceCharge ? 0 : serviceChargeAmount}
      serviceChargePercentage={hideServiceCharge ? 0 : serviceChargePercentage}
      currency={currency}
      countryCode={countryCode}
      taxInclusive={customerPreOrder?.tax_inclusive}
      taxBreakdown={customerPreOrder?.tax_breakdown}
      totalNet={customerPreOrder?.total_net}
      totalGross={customerPreOrder?.total_gross}
      taxLabel={customerPreOrder?.tax_label}
    />
  );

  // Check if any item in the basket has hidden prices (not all menus in settings)
  const shouldHideTotal = items.some((item) => item.quantity > 0 && hidePricesByMenu[item.menu_id]);

  const isTaxExclusive =
    customerPreOrder?.tax_inclusive === false &&
    Array.isArray(customerPreOrder?.tax_breakdown) &&
    customerPreOrder.tax_breakdown.length > 0;

  const getButtonText = () => {
    if (shouldHideTotal) {
      return t("place_order_button", "Place your order");
    }
    // For tax-exclusive venues, show the gross total so the button matches the pricing summary.
    const buttonAmount =
      isTaxExclusive && customerPreOrder?.total_gross != null
        ? customerPreOrder.total_gross
        : (totalCostWithService ?? totalCost ?? 0);
    return `${t("place_order_button", "Place your order")} \u2022 ${formatCurrency(buttonAmount, currency, countryCode)}`;
  };

  return (
    <div id="gc-basket-footer">
      {showFooterInfo ? (
        <div className="pre-order-footer">
          {!shouldHideTotal && <div className="footer-desktop-container-total">{renderPricing()}</div>}
          {/* Match the .container-payment inset so the notice aligns with the button. */}
          <div className="gcw:px-bs-3">
            <SlowRequestNotice />
          </div>
          <div className="container-payment gcw:flex gcw:items-center gcw:justify-center">
            {!submitHidden && (
              <PrimaryButton
                text={getButtonText()}
                className={
                  "gcw:inline-flex gcw:min-h-12 gcw:items-center gcw:justify-center gcw:rounded-md gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:text-lg gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:disabled:opacity-50 gcw:disabled:cursor-not-allowed gcw:disabled:hover:bg-primary pay-button"
                }
                onClick={() => !isDisabled && goToNextStep()}
                disabled={isDisabled}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="pre-order-footer">
          {/* Match the .container-payment inset so the notice aligns with the button. */}
          <div className="gcw:px-bs-3">
            <SlowRequestNotice />
          </div>
          <div className="container-payment gcw:flex gcw:items-center">
            {!submitHidden && (
              <PrimaryButton
                text={getButtonText()}
                className={
                  "gcw:inline-flex gcw:min-h-12 gcw:items-center gcw:justify-center gcw:rounded-md gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:text-lg gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:disabled:opacity-50 gcw:disabled:cursor-not-allowed gcw:disabled:hover:bg-primary pay-button"
                }
                onClick={() => !isDisabled && goToNextStep()}
                disabled={isDisabled}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export { BasketFooter };
export default BasketFooter;
